var _ = require('underscore');
var TorsoFormView = require('backbone-torso/modules/FormView');
var PaymentDetailsAchView = require('./../payment/PaymentDetailsAchView');
var PaymentDetailsCreditCardView = require('./../payment/PaymentDetailsCreditCardView');
var PaymentDetailsFormModel = require('./../payment/PaymentDetailsFormModel');
var patientPaymentMethodEditTemplate = require('./patientPaymentMethodEditTemplate.hbs');
var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');
var StoredPaymentMethodService = require('./StoredPaymentMethodService');
var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var applicationUtils = require('./../applicationUtils');

var PAYMENT_METHODS = PaymentDetailsFormModel.PAYMENT_METHODS;

/**
 * Form view for adding a new stored payment method
 *
 * @class PatientStoredPaymentMethodView
 * @constructor
 * @param options {Object} Options to configure this perspective.
 *   @param [options.patientVecnaId] {String} the vecna id of the current patient
 */
module.exports = TorsoFormView.extend({
  tagName: 'form',
  className: 'brec-form',
  template: patientPaymentMethodEditTemplate,
  events: {
    'submit': 'submit',
    'focus input[type="radio"]': '_setFocusOutline',
    'focus input[type="checkbox"]': '_setFocusOutline',
    'blur input[type="radio"]': '_removeFocusOutline',
    'blur input[type="checkbox"]': '_removeFocusOutline',
    'keyup #store-payment_save-true': '_setFocusOnCardNickname'
  },

  behaviors: {
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      id: { property: '_patientVecnaId' }
    }
  },

  feedback: [
    /**
     * When model has validation error,
     * Then disable the Save payment method button
     * @property feedback[0] (Disable/Enable Save payment method button) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          },
          {
            object: function() {
              return this.getBehavior('demographics');
            },
            events: 'change:fetchSuccess'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        var fetchDemographicsSuccess = this.getBehavior('demographics').get('fetchSuccess');
        var hasPatientVecnaId = this._patientVecnaId;

        if (( noErrors || cardNeedsEncryption ) && fetchDemographicsSuccess && hasPatientVecnaId) {
          return {
            removeClass: 'brec-btn--disabled',
            removeAttr: ['disabled', 'disabled']
          };
        } else {
          return {
            addClass: 'brec-btn--disabled',
            attr: ['disabled', 'disabled']
          };
        }
      },
      to: 'save-payment-method'
    },
    /**
     * When model has validation error,
     * Then show the warning feedback box and set "aria-hidden" to "true".
     * @property feedback[1] (Show validation warning feedback box) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');

        if ( noErrors || cardNeedsEncryption ) {
          return {
            addClass: 'brec-hidden',
            attr: ['aria-hidden', 'true']
          };
        } else {
          return {
            removeClass: 'brec-hidden',
            attr: ['aria-hidden', 'false']
          };
        }
      },
      to: 'addPaymentMethodValidationWarningBox'
    },
    /**
     * When model has validation error,
     * Then display the first error as a warning message.
     * @property feedback[2] (Add Payment Method validation warning) {When/Then Feedback}
     */
    {
      when: {
        listenTo: [
          {
            object: 'model',
            events: 'change'
          }
        ]
      },
      then: function() {
        var errors = this.model.validate();
        errors = _.keys(errors);
        var noErrors = errors.length === 0;
        var cardNeedsEncryption = errors.length === 1 && _.contains(errors, 'cardData.encryptedCardInfo');
        if ( noErrors || cardNeedsEncryption) {
          return {
            text: ''
          };
        } else if (errors[0] === 'cardData.encryptedCardInfo') {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[1])
          };
        } else {
          return {
            text: $.i18n.prop('payment.validationWarning.' + errors[0])
          };
        }
      },
      to: 'addPaymentMethodValidationWarning'
    },{
      /**
       * On click of the payment method radio/checkbox,
       * Then remove existing focus container class,
       * And add it to the container of the selected radio/checkbox
       * @property feedback[3] (Payment Method Radio/Checkbox Container) {When/Then Feedback}
       */
      when: {
        '@paymentMethod': ['change']
      },
      then: function (event) {
        var focusContainer = this.$el.find('[data-focus-container]');
        if (focusContainer.hasClass('brec-form__focus-container')) {
          focusContainer.removeClass('brec-form__focus-container--focused');
        }
        var targetContainer = $(event.target).closest('[data-focus-container]');
        targetContainer.addClass('brec-form__focus-container--focused');
      },
      to: 'save-payment-method'
    }
  ],

  /**
   * @method initialize
   * @override
   * @param options {Object} Options to configure this perspective.
   *   @param [options.patientVecnaId] {String} the vecna id of the current patient
   */
  initialize: function(options) {
    _.bindAll(this, 'submit', '_rejectPromiseIfDemographicsFetchFailed', '_copyPatientIdentifiersFromDemographics');
    options = options || {};
    this._patientVecnaId = options.patientVecnaId;

    this._setAttributes({
      'action': applicationUtils.currentApplication.fullUrlPath + '#patient/' + this._patientVecnaId + '/profile'
    });

    this.model = new PaymentDetailsFormModel({
      isStoredPayment: true
    }, {
      isMakingPayment: false
    });

    this._paymentDetailsAchView = new PaymentDetailsAchView({ model: this.model });
    this.registerTrackedView(this._paymentDetailsAchView);
    this._paymentDetailsCreditCardView = new PaymentDetailsCreditCardView({ model: this.model });
    this.registerTrackedView(this._paymentDetailsCreditCardView);

    this._storedPaymentMethodService = new StoredPaymentMethodService({
      formModel: this.model,
      patientVecnaId: this._patientVecnaId
    });

    this.listenTo(this.getBehavior('demographics'), 'fetched change:acceptedDemographics.contactInfo.name', this._defaultAccountOwnerName);
    this._defaultAccountOwnerName();

    this.listenTo(this.model, 'change:paymentMethod', this._activatePaymentMethod);
    this.listenTo(this.model, 'change:paymentMethod', this.render);
    this.listenTo(financialFeatureConfigModel, 'load-complete', this._setPaymentDefaults);
    this.listenTo(financialFeatureConfigModel, 'load-complete', this.render);
    this.listenTo(this._storedPaymentMethodService, 'change:errors', this.render);
    this.listenTo(this.viewState, 'change:errors', this.render);
    _.bindAll(this, '_setFinancialConfigLoaded');
    financialFeatureConfigModel.loadOnce()
      .then(this._setFinancialConfigLoaded);
    this._setPaymentDefaults();
  },

  /**
   * Set any payment defaults that are implied by the financial feature configuration
   * @method _setPaymentDetails
   * @private
   */
  _setPaymentDefaults: function() {
    var creditCardPaymentEnabled = financialFeatureConfigModel.get('normal.creditCardPaymentEnabled'),
        achPaymentEnabled = financialFeatureConfigModel.get('normal.achPaymentEnabled'),
        paymentSelectionEnabled = creditCardPaymentEnabled && achPaymentEnabled;

    //if not allowing user to choose payment method, make sure model knows which one we're defaulting to
    if (!paymentSelectionEnabled) {
      if (creditCardPaymentEnabled) {
        this.model.set('paymentMethod', PAYMENT_METHODS.creditCard);
      }
      if (achPaymentEnabled) {
        this.model.set('paymentMethod', PAYMENT_METHODS.ach);
      }
    }

    this._activatePaymentMethod();
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var context = TorsoFormView.prototype.prepare.apply(this);
    var creditCardPaymentEnabled = financialFeatureConfigModel.get('normal.creditCardPaymentEnabled');
    var achPaymentEnabled = financialFeatureConfigModel.get('normal.achPaymentEnabled');

    context.patientVecnaId = this._patientVecnaId;
    context.creditCardPaymentEnabled = creditCardPaymentEnabled;
    context.achPaymentEnabled = achPaymentEnabled;
    context.multiplePaymentMethods = creditCardPaymentEnabled && achPaymentEnabled;
    context.noPaymentMethods = !creditCardPaymentEnabled && !achPaymentEnabled;
    context.PAYMENT_METHODS = PAYMENT_METHODS;
    context.loading = financialFeatureConfigModel.loading;
    context.paymentMethod = this.model.get('paymentMethod');
    context.currentApplication = applicationUtils.currentApplication;

    var serviceErrors = this._storedPaymentMethodService.get('errors');
    context.errors = _.filter(serviceErrors);
    return context;
  },

  /**
   * @method attachTrackedViews
   * @override
   */
  attachTrackedViews: function() {
    if (this._paymentDetailsViewConfiguration) {
      this.attachView(this._paymentDetailsViewConfiguration.injectionSite, this._paymentDetailsViewConfiguration.view);
    }
  },

  /**
   * On submit create a stored payment methods and update it with external token when received
   *
   * @method submit
   * @param event {Event}
   *   @param event.target.action {String} the action url of the form that was submitted.
   */
  submit: function(event) {
    if (event) {
      event.preventDefault();
    }
    if (this._paymentDetailsViewConfiguration && this._paymentDetailsViewConfiguration.view) {
      this.listenTo(this.model, 'paymentDataReady', function() {
        this._processPayment(event.target.action);
      });
      this._paymentDetailsViewConfiguration.view.trigger('submit');
    }
  },

  /**
   * Complete the payment process after scripts and whatever are loaded.
   * @method _processPayment
   * @param completeUrl {String} the url to direct to on successful saving of the card data.
   * @private
   */
  _processPayment: function(completeUrl) {
    var errors = this.model.validate();
    if (!errors) {
      this._getDemographicsFetchedPromise()
        .then(this._rejectPromiseIfDemographicsFetchFailed)
        .then(this._copyPatientIdentifiersFromDemographics)
        .then(this._storedPaymentMethodService.createStoredPaymentMethod)
        .then(function() {
          window.location.href = completeUrl;
        });
    }
  },

  /**
   * Default the account owner name to the current user if it has not already been set on the form model
   *
   * @method _defaultAccountOwnerName
   * @private
   */
  _defaultAccountOwnerName: function() {
    var name = this.getBehavior('demographics').data.get('acceptedDemographics.contactInfo.name');
    var accountOwnerName = name && name.givenName + ' ' + name.familyName;
    var shouldDefaultAccountOwnerName = accountOwnerName && !this.model.get('accountOwnerName');

    if (shouldDefaultAccountOwnerName) {
      this.model.set('accountOwnerName', accountOwnerName);
    }
  },

  /**
   * This method attempts to take the event and state based fetch status of demographics data behavior and convert it into
   * a promise that can be used in a promise chain.
   * @method _getDemographicsFetchedPromise
   * @return {jQuery.Promise} a promise that resolves with the fetch result once the demographics have been fetched.
   * @private
   */
  _getDemographicsFetchedPromise: function() {
    var demographicsFetchedDeferred = $.Deferred();

    var demographicsDataBehavior = this.getBehavior('demographics');
    var demographicFetchSuccess = demographicsDataBehavior.get('fetchSuccess');
    if (_.isUndefined(demographicFetchSuccess)) {
      this.listenToOnce(demographicsDataBehavior, 'fetched', function() {
        var demographicFetchSuccess = demographicsDataBehavior.get('fetchSuccess');
        demographicsFetchedDeferred.resolve(demographicFetchSuccess);
      });
    } else {
      demographicsFetchedDeferred.resolve(demographicFetchSuccess);
    }
    return demographicsFetchedDeferred.promise();
  },

  /**
   * @method _rejectPromiseIfDemographicsFetchFailed
   * @param fetchSuccess {Boolean} true if the fetch was successful, false otherwise.
   * @return {Boolean} true if the fetch was successful.  Otherwise it will throw an error.
   * @throws {Error} (which will result in a rejected promise) if the demographics failed to be retrieved successfully.
   * @private
   */
  _rejectPromiseIfDemographicsFetchFailed: function(fetchSuccess) {
    if (fetchSuccess) {
      return fetchSuccess;
    }
    throw new Error('Failed to fetch demographics.');
  },

  /**
   * @method _copyPatientIdentifiersFromDemographics
   * @private
   */
  _copyPatientIdentifiersFromDemographics: function() {
    var demographicsDataBehavior = this.getBehavior('demographics');
    var patientIdentifiers = demographicsDataBehavior.data.get('identifiers');
    this.model.set('patientIdentifiers', patientIdentifiers);
  },

  /**
   * Sets the given payment method view as active (for validation).
   * @method _activatePaymentMethod
   * @private
   */
  _activatePaymentMethod: function() {
    var paymentMethod = this.model.get('paymentMethod'),
      creditCardPaymentEnabled = financialFeatureConfigModel.get('normal.creditCardPaymentEnabled'),
      achPaymentEnabled = financialFeatureConfigModel.get('normal.achPaymentEnabled');

    // For backwards compatibility if the payment method is not defined, then assume it is ACH.
    if (paymentMethod === PAYMENT_METHODS.ach && achPaymentEnabled) {
      this._paymentDetailsCreditCardView.activate();
      this._paymentDetailsAchView.deactivate();
      this._paymentDetailsViewConfiguration = {
        injectionSite: 'new-payment-method-ach',
        view: this._paymentDetailsAchView
      };
    } else if (paymentMethod === PAYMENT_METHODS.creditCard && creditCardPaymentEnabled) {
      this._paymentDetailsCreditCardView.deactivate();
      this._paymentDetailsAchView.activate();
      this._paymentDetailsViewConfiguration = {
        injectionSite: 'new-payment-method-credit-card',
        view: this._paymentDetailsCreditCardView
      };
    }
  },

  /**
   * Marks that the financial feature config has finished loading
   * @method setFinancialConfigLoaded
   * @private
   */
   _setFinancialConfigLoaded: function() {
     this.set('financialConfigLoaded', true);
   },

  /**
   * @method _dispose
   * @private
   * @override
   */
  _dispose: function() {
    this._storedPaymentMethodService.dispose();
  },

  /**
   * A method to add focus container class to the container of
   * a radio/checkbox when it receives focus.
   * @method _setFocusOutline
   * @private
   */
  _setFocusOutline: function(event) {
    $(event.target).closest('[data-focus-container]').addClass('brec-form__focus-container--focused');
  },

  /**
   * A method to remove focus container class of the container of
   * a radio/checkbox when it loses focus
   * @method _fetchStoredPaymentMethods
   * @private
   */
  _removeFocusOutline: function(event) {
    $(event.target).closest('[data-focus-container]').removeClass('brec-form__focus-container--focused');
  }
});
